import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Filter, ArrowUpAZ, ArrowDownAZ, Check } from 'lucide-react';


const FilterableDataGrid = ({
  data,
  allData, // Full unfiltered data for calculating filter options
  columns: initialColumns,
  onRowClick,
  onRowDoubleClick,
  filters,
  setFilters,
  // Column customization props (external state from header)
  columnOrder: externalColumnOrder,
  hiddenColumns: externalHiddenColumns,
  columnWidths: externalColumnWidths,
  onColumnResize,
  // Edit mode props
  editModeEnabled = false,
  editedData = {},           // { rowId: { fieldName: value } }
  renderEditableCell,        // (row, column, value, isEdited) => JSX
  // Delete mode props
  deleteModeEnabled = false,
  selectedForDelete = new Set(),
  onToggleSelectRow,
  onSelectAllOnPage,
  // Frozen columns
  frozenColumns = [],
  // Row class callback
  getRowClassName,           // (row) => string — extra class(es) for the <tr>
  // Grouped column headers (optional). When provided, renders a second header
  // row above the column labels with cells spanning grouped columns.
  //   sectionGroups={[
  //     { label: 'Actual IO Counts', keys: ['act_AI','act_AO', ...] },
  //     { label: 'Card Density',     keys: ['den_AI','den_AO', ...] },
  //   ]}
  // Columns not listed in any group render as blank spacers in the section row.
  sectionGroups = null,
}) => {
  // Use allData for filter options if provided, otherwise fall back to data
  const optionsData = allData || data;
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState({});
  const [pendingSelections, setPendingSelections] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const filterRef = useRef(null);
  const headerRefs = useRef({});
  const pendingDropdownPos = useRef({ top: 0, left: 0 });

  // Column customization state - use external if provided, otherwise internal
  const [internalColumnOrder, setInternalColumnOrder] = useState(() => initialColumns.map(col => col.key));
  const [internalHiddenColumns] = useState(new Set());
  const [internalColumnWidths, setInternalColumnWidths] = useState(() => {
    const widths = {};
    initialColumns.forEach(col => {
      widths[col.key] = col.width || 150;
    });
    return widths;
  });

  // Column resize state
  const [resizing, setResizing] = useState(null); // { key, startX, startWidth }
  const tableRef = useRef(null);

  // Use external state if provided (and non-empty), otherwise use internal
  const columnOrder = (externalColumnOrder?.length > 0) ? externalColumnOrder : internalColumnOrder;
  const hiddenColumns = (externalHiddenColumns?.length > 0) ? new Set(externalHiddenColumns) : internalHiddenColumns;
  const columnWidths = externalColumnWidths || internalColumnWidths;

  // Update internal column order when initialColumns change (only if using internal state)
  useEffect(() => {
    if (!externalColumnOrder?.length) {
      setInternalColumnOrder(prev => {
        const newKeys = initialColumns.map(col => col.key);
        const prevSet = new Set(prev);
        const newSet = new Set(newKeys);
        // If the set of keys changed, reset to initialColumns order (respects upstream ordering)
        const keysChanged = newKeys.some(k => !prevSet.has(k)) || prev.some(k => !newSet.has(k));
        if (!keysChanged) return prev;
        return newKeys;
      });
    }
  }, [initialColumns, externalColumnOrder]);

  // Get visible columns in current order
  const visibleColumns = columnOrder
    .filter(key => !hiddenColumns.has(key))
    .map(key => initialColumns.find(col => col.key === key))
    .filter(Boolean);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Column resize handlers
  const handleResizeStart = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[colKey] || 150;
    setResizing({ key: colKey, startX: e.clientX, startWidth });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff); // Min width 50px

      if (onColumnResize) {
        onColumnResize(resizing.key, newWidth);
      } else {
        setInternalColumnWidths(prev => ({
          ...prev,
          [resizing.key]: newWidth
        }));
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, onColumnResize]);

  const getColumnOptions = (colKey) => {
    const otherFilters = Object.entries(filters).filter(([key]) => key !== colKey);
    const baseData = optionsData.filter(row =>
      otherFilters.every(([key, vals]) => !vals || vals.length === 0 || vals.includes(String(row[key] || '')))
    );
    return [...new Set(baseData.map(row => String(row[colKey] || '')))].sort();
  };

  const getFilteredOptions = (colKey) => {
    const options = getColumnOptions(colKey);
    const search = (filterSearch[colKey] || '').toLowerCase();
    return search ? options.filter(opt => opt.toLowerCase().includes(search)) : options;
  };

  const openFilterDropdown = (colKey) => {
    const header = headerRefs.current[colKey];
    if (header) {
      const rect = header.getBoundingClientRect();
      const dropdownHeight = 380;
      const dropdownWidth = 240;

      let top = rect.bottom + 4;
      let left = rect.left;

      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 4;
      }

      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 8;
      }

      pendingDropdownPos.current = { top, left };
    }
    setPendingSelections(prev => ({ ...prev, [colKey]: filters[colKey] || [] }));
    setOpenFilter(colKey);
  };

  useLayoutEffect(() => {
    if (openFilter && filterRef.current) {
      filterRef.current.style.top = `${pendingDropdownPos.current.top}px`;
      filterRef.current.style.left = `${pendingDropdownPos.current.left}px`;
    }
  }, [openFilter]);

  const handleCheckboxChange = (colKey, value) => {
    setPendingSelections(prev => {
      const current = prev[colKey] || [];
      return {
        ...prev,
        [colKey]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  const handleSelectAll = (colKey) => {
    const allOptions = getFilteredOptions(colKey);
    const currentSelections = pendingSelections[colKey] || [];
    const allSelected = allOptions.every(opt => currentSelections.includes(opt));
    
    if (allSelected) {
      // Deselect all filtered options
      setPendingSelections(prev => ({
        ...prev,
        [colKey]: currentSelections.filter(sel => !allOptions.includes(sel))
      }));
    } else {
      // Select all filtered options
      setPendingSelections(prev => ({
        ...prev,
        [colKey]: [...new Set([...currentSelections, ...allOptions])]
      }));
    }
  };

  const applyFilter = (colKey) => {
    const selected = pendingSelections[colKey] || [];
    const allOptions = getColumnOptions(colKey);
    // If all options selected, clear the filter (no filtering needed)
    const isAllSelected = allOptions.length > 0 && allOptions.every(opt => selected.includes(opt));
    setFilters(prev => ({ ...prev, [colKey]: isAllSelected ? [] : selected }));
    setOpenFilter(null);
  };

  const cancelFilter = () => setOpenFilter(null);

  const getCurrentSelections = (colKey) => {
    return openFilter === colKey ? (pendingSelections[colKey] || []) : (filters[colKey] || []);
  };

  const isAllSelected = (colKey) => {
    const allOptions = getFilteredOptions(colKey);
    const currentSelections = getCurrentSelections(colKey);
    return allOptions.length > 0 && allOptions.every(opt => currentSelections.includes(opt));
  };

  const handleSort = (colKey, direction) => {
    setSortConfig({ key: colKey, direction });
    setOpenFilter(null);
  };

  // Filter data based on column filters
  const getFilteredData = () => {
    if (!filters || Object.keys(filters).length === 0) return data;

    return data.filter(row => {
      return Object.entries(filters).every(([colKey, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        const cellValue = String(row[colKey] || '');
        return selectedValues.includes(cellValue);
      });
    });
  };

  const filteredData = getFilteredData();

  const getSortedData = () => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = String(a[sortConfig.key] || '').toLowerCase();
      const bVal = String(b[sortConfig.key] || '').toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = getSortedData();

  // eslint-disable-next-line no-unused-vars
  const hasActiveFilters = filters && Object.values(filters).some(f => f?.length > 0);

  // Calculate total table width from column widths (add 40px for delete checkbox column if enabled)
  const tableWidth = visibleColumns.reduce((sum, c) => sum + (columnWidths[c.key] || c.width || 150), 0) + (deleteModeEnabled ? 40 : 0);

  useLayoutEffect(() => {
    if (!tableRef.current) return;
    tableRef.current.style.width = `${tableWidth}px`;
    const cols = tableRef.current.querySelectorAll('col');
    const offset = deleteModeEnabled ? 1 : 0;
    visibleColumns.forEach((col, idx) => {
      if (cols[idx + offset]) cols[idx + offset].style.width = `${columnWidths[col.key] || col.width || 150}px`;
    });
  }, [tableWidth, columnWidths, visibleColumns, deleteModeEnabled]);

  // Compute left offsets for sticky frozen columns
  const frozenSet = new Set(frozenColumns);
  const frozenLeftOffsets = {};
  if (frozenSet.size > 0) {
    let leftAccum = deleteModeEnabled ? 40 : 0;
    visibleColumns.forEach(col => {
      if (frozenSet.has(col.key)) frozenLeftOffsets[col.key] = leftAccum;
      leftAccum += columnWidths[col.key] || col.width || 150;
    });
  }

  // Check if all visible rows are selected for delete
  const allOnPageSelected = deleteModeEnabled && sortedData.length > 0 && sortedData.every(row => selectedForDelete.has(row.id));

  return (
    <div className={`stui-data-grid-container ${resizing ? 'is-resizing' : ''}`}>
      <div className="stui-data-grid-scroll-area">
      <table ref={tableRef} className={`stui-data-grid${editModeEnabled ? ' edit-mode' : ''}${deleteModeEnabled ? ' delete-mode' : ''}`}>
        <colgroup>
          {deleteModeEnabled && <col className="stui-col-delete" />}
          {visibleColumns.map(col => (
            <col key={col.key} />
          ))}
        </colgroup>
        <thead>
          {sectionGroups && sectionGroups.length > 0 && (() => {
            // Build the top row by walking visibleColumns and merging adjacent
            // columns that belong to the same group. Columns not in any group
            // become blank spacer cells.
            const keyToGroup = new Map();
            sectionGroups.forEach(g => (g.keys || []).forEach(k => keyToGroup.set(k, g)));
            const topCells = [];
            let curGroup = null, curSpan = 0;
            visibleColumns.forEach(col => {
              const g = keyToGroup.get(col.key) ?? null;
              if (g === curGroup) {
                curSpan++;
              } else {
                if (curSpan > 0) topCells.push({ group: curGroup, span: curSpan });
                curGroup = g;
                curSpan = 1;
              }
            });
            if (curSpan > 0) topCells.push({ group: curGroup, span: curSpan });
            return (
              <tr className="stui-data-grid-section-row">
                {deleteModeEnabled && <th className="stui-data-grid-section-spacer" />}
                {topCells.map((cell, i) => (
                  <th
                    key={i}
                    colSpan={cell.span}
                    className={cell.group ? 'stui-data-grid-section-cell' : 'stui-data-grid-section-spacer'}
                  >
                    {cell.group?.label || ''}
                  </th>
                ))}
              </tr>
            );
          })()}
          <tr>
            {deleteModeEnabled && (
              <th className="stui-delete-col">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={onSelectAllOnPage}
                  title="Select all on page"
                />
              </th>
            )}
            {visibleColumns.map(col => {
              const hasFilter = filters[col.key]?.length > 0;
              const isSorted = sortConfig.key === col.key;
              const isHovered = hoveredColumn === col.key;
              const isVertical = col.headerVertical;
              const isFrozen = frozenSet.has(col.key);
              const thClasses = [
                isVertical ? 'header-rotate-90' : '',
                'filterable-header',
                isFrozen ? 'frozen' : ''
              ].filter(Boolean).join(' ');
              const frozenStyle = isFrozen ? { position: 'sticky', left: frozenLeftOffsets[col.key], zIndex: 4 } : undefined;
              return (
                <th
                  key={col.key}
                  ref={el => headerRefs.current[col.key] = el}
                  className={thClasses}
                  style={frozenStyle}
                  onClick={() => openFilterDropdown(col.key)}
                  onMouseEnter={() => setHoveredColumn(col.key)}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  <span className={`column-label ${isVertical ? 'header-content' : ''}`}>{col.label}</span>
                  <Filter
                    size={12}
                    className={`header-filter-icon ${isHovered || hasFilter || isSorted ? 'visible' : ''} ${hasFilter || isSorted ? 'active' : ''}`}
                  />
                  <div
                    className="stui-column-resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => {
            const isSelectedForDelete = deleteModeEnabled && selectedForDelete.has(row.id);
            const extraClass = getRowClassName ? getRowClassName(row) : '';
            return (
            <tr
              key={row.id || idx}
              onClick={() => onRowClick?.(row)}
              onDoubleClick={() => onRowDoubleClick?.(row)}
              className={[onRowClick || onRowDoubleClick ? 'stui-clickable' : '', isSelectedForDelete ? 'selected-for-delete' : '', extraClass].filter(Boolean).join(' ') || undefined}
            >
              {deleteModeEnabled && (
                <td className="stui-delete-col">
                  <input
                    type="checkbox"
                    checked={isSelectedForDelete}
                    onChange={() => onToggleSelectRow?.(row.id)}
                  />
                </td>
              )}
              {visibleColumns.map(col => {
                const value = row[col.key];
                const rowId = row.id;
                const isFrozen = frozenSet.has(col.key);
                const frozenStyle = isFrozen ? { position: 'sticky', left: frozenLeftOffsets[col.key], zIndex: 1 } : undefined;
                const alignClass = col.alignment === 'right' ? ' stui-cell-right' : col.alignment === 'center' ? ' stui-cell-center' : '';
                const frozenClass = isFrozen ? ' frozen' : '';
                const isEdited = editedData[rowId]?.[col.fieldName] !== undefined;
                const displayValue = isEdited ? editedData[rowId][col.fieldName] : value;

                // Edit mode: use custom renderer if provided
                if (editModeEnabled && renderEditableCell) {
                  return (
                    <td key={col.key} className={`editable-cell${isEdited ? ' modified' : ''}${frozenClass}${alignClass}`} style={frozenStyle} onDoubleClick={e => e.stopPropagation()}>
                      {renderEditableCell(row, col, displayValue, isEdited)}
                    </td>
                  );
                }

                // Read mode: render boolean values as checkboxes
                if (typeof value === 'boolean') {
                  return (
                    <td key={col.key} className={`cell-boolean${frozenClass}${alignClass}`} style={frozenStyle}>
                      <div className="stui-boolean-display">
                        {value ? <Check size={16} className="stui-boolean-check" /> : null}
                      </div>
                    </td>
                  );
                }
                const renderedValue = col.renderCell ? col.renderCell(row, col, value) : col.formatter ? col.formatter(value) : value;
                const tdClass = (frozenClass + alignClass).trim() || undefined;
                return <td key={col.key} className={tdClass} style={frozenStyle}>{renderedValue}</td>;
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {openFilter && (
        <div
          ref={filterRef}
          className="stui-filter-dropdown"
          onClick={e => e.stopPropagation()}
        >
          <div className="stui-filter-sort-section">
            <button
              className={`stui-filter-sort-btn ${sortConfig.key === openFilter && sortConfig.direction === 'asc' ? 'active' : ''}`}
              onClick={() => handleSort(openFilter, 'asc')}
            >
              <ArrowUpAZ size={16} />
              <span>Sort A to Z</span>
            </button>
            <button
              className={`stui-filter-sort-btn ${sortConfig.key === openFilter && sortConfig.direction === 'desc' ? 'active' : ''}`}
              onClick={() => handleSort(openFilter, 'desc')}
            >
              <ArrowDownAZ size={16} />
              <span>Sort Z to A</span>
            </button>
          </div>

          <div className="stui-filter-divider" />

          <div className="stui-filter-search">
            <input
              type="text"
              placeholder="Search"
              value={filterSearch[openFilter] || ''}
              onChange={e => setFilterSearch(prev => ({
                ...prev,
                [openFilter]: e.target.value
              }))}
            />
          </div>

          <div className="stui-filter-options">
            <label className="stui-filter-option stui-filter-option-select-all">
              <input
                type="checkbox"
                checked={isAllSelected(openFilter)}
                onChange={() => handleSelectAll(openFilter)}
              />
              <span>(Select All)</span>
            </label>
            {getFilteredOptions(openFilter).map(option => (
              <label key={option} className="stui-filter-option">
                <input
                  type="checkbox"
                  checked={getCurrentSelections(openFilter).includes(option)}
                  onChange={() => handleCheckboxChange(openFilter, option)}
                />
                <span>{option || '(empty)'}</span>
              </label>
            ))}
          </div>

          <div className="stui-filter-footer">
            <button onClick={() => applyFilter(openFilter)} className="stui-filter-ok-btn">OK</button>
            <button onClick={cancelFilter} className="stui-filter-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterableDataGrid;
